// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — editing a Khipu's path (the Khipu plan, the editor, 2026-10-08),
 * without the DOM: what the Khipu editor (`khipuEditorUI`) does to a story
 * or a briefing, as plain functions that return a new Khipu and leave the
 * one given untouched.
 *
 * - `stepFromGlobe`: a step from what the globe shows (dataset, camera,
 *   date, playing or held, overlay), adding the dataset and the overlay to
 *   the Khipu's datasets when they are new. The Studio panel uses it too.
 * - `insertStep`, `moveStep`, `removeStep`, `updateStep`, `sceneToStep`:
 *   the path, renumbered after every change (ids are kept, so a log that
 *   names a step still finds it).
 * - `setText`: a field in one language, the others kept.
 * - `editProblems`: what stops it being saved (an http link, a timer under a
 *   second, a quiz without two answers), as message keys with the step.
 *
 * Only the path and the title are edited. Everything else a Khipu carries
 * (its log, a Studio shot, credits, unknown fields) passes through as it
 * was. Written from scratch for AYNI: nothing here comes from TerraViz.
 */

import type { Khipu, KhipuDatasetRef, KhipuStep, KhipuText } from './khipu'
import { KHIPU_SCHEMA_VERSION } from './khipu'
import { roundScenePins, roundSceneView, sceneRtLayer, streamRefOf, type SoloScene } from './soloRecorder'

/** A field in `lang`, the other languages kept; an empty value removes that language. */
export function setText(text: KhipuText | undefined, lang: string, value: string): KhipuText | undefined {
  const next: Record<string, string> = { ...(text ?? {}) }
  const clean = value.trim()
  if (clean) next[lang] = value
  else delete next[lang]
  return Object.keys(next).length ? next : undefined
}

export interface SceneTitles {
  titleOf?: (datasetId: string) => string | null
  layerTitleOf?: (layerId: string) => string | null
}

/** The key of a dataset reference, added to the Khipu's datasets when new. */
function refKey(datasets: KhipuDatasetRef[], ref: string, title: string, appId?: string, dsaId?: string): { datasets: KhipuDatasetRef[]; key: string } {
  const found = datasets.find((d) => d.ref === ref || (dsaId && d.dsaId === dsaId && d.ref.startsWith('stream:')))
  if (found) return { datasets, key: found.key }
  const used = new Set(datasets.map((d) => d.key))
  let n = datasets.length + 1
  while (used.has(`d${n}`)) n++
  const key = `d${n}`
  return { datasets: [...datasets, { key, ref, ...(appId ? { appId } : {}), ...(dsaId ? { dsaId } : {}), title: { en: title } }], key }
}

/** A fresh step id, not used by any step of the path. */
function freshId(steps: readonly KhipuStep[]): string {
  const used = new Set(steps.map((s) => s.id))
  let n = steps.length + 1
  while (used.has(`s${n}`)) n++
  return `s${n}`
}

/** The parts of a step the globe decides, from a scene, with the datasets it needs. */
function sceneParts(k: Khipu, scene: SoloScene, titles: SceneTitles, keepNotes: KhipuStep['pins'] = [], keepLayers: KhipuStep['layers'] = []): { datasets: KhipuDatasetRef[]; parts: Pick<KhipuStep, 'dataset' | 'view' | 'time' | 'layers' | 'autorotate' | 'pins' | 'layout' | 'globes'> } {
  let datasets = k.datasets
  let dataset: string | null = null
  if (scene.datasetId) {
    const r = refKey(datasets, `app:${scene.datasetId}`, titles.titleOf?.(scene.datasetId) ?? scene.datasetId, scene.datasetId)
    datasets = r.datasets
    dataset = r.key
  }
  const layers: NonNullable<KhipuStep['layers']> = []
  for (const id of scene.overlays) {
    const r = refKey(datasets, `layer:${id}`, titles.layerTitleOf?.(id) ?? id)
    datasets = r.datasets
    layers.push({ role: 'overlay', dataset: r.key })
  }
  // AYNI (owner, 2026-10-12): the real-time overlays, with their opacity and eye; one the step had keeps its question.
  for (const rt of scene.realtime ?? []) {
    const stream = streamRefOf(rt.id)
    if (!stream) continue
    const r = refKey(datasets, stream.ref, titles.titleOf?.(rt.id) ?? rt.id, undefined, stream.dsaId)
    datasets = r.datasets
    const had = (keepLayers ?? []).find((l) => l.role === 'realtime' && l.dataset === r.key)
    layers.push({ ...sceneRtLayer(rt, r.key), ...(had?.prompt ? { prompt: had.prompt } : {}) })
  }
  // The pins on the globe; one where the step already had a pin keeps that pin's note.
  const before = (keepNotes ?? []) as Array<{ lat: number; lon: number; note?: unknown }>
  const pins = roundScenePins(scene.pins).map((q) => {
    const same = before.find((b) => Math.abs(b.lat - q.lat) <= 0.001 && Math.abs(b.lon - q.lon) <= 0.001)
    return { lat: q.lat, lon: q.lon, color: q.color, ...(same?.note ? { note: same.note } : {}) }
  })
  // AYNI (multi-globe plan): the other globes, by position, each its dataset and time rule; one globe says so too.
  const many = !!scene.layout && scene.layout !== '1' && !!scene.globes
  const globes: NonNullable<KhipuStep['globes']> = []
  for (const g of many ? scene.globes! : []) {
    let key: string | null = null
    if (g.datasetId) {
      const r = refKey(datasets, `app:${g.datasetId}`, titles.titleOf?.(g.datasetId) ?? g.datasetId, g.datasetId)
      datasets = r.datasets
      key = r.key
    }
    globes.push({ dataset: key, ...(g.time ? { time: g.time } : {}) })
  }
  return {
    datasets,
    parts: {
      // Only with several globes; one globe clears them (updateStep removes a field set to undefined).
      ...(many ? { layout: scene.layout, globes } : { layout: undefined, globes: undefined }),
      dataset,
      // With its rotation (bearing) and tilt (owner, 2026-10-09).
      view: scene.view ? roundSceneView(scene.view) : null,
      pins,
      time: scene.date ? { at: scene.date, playing: scene.playing === true } : null,
      layers,
      autorotate: scene.spin ? { on: true, speed: Math.round(scene.spin * 100) / 100 } : { on: false },
    },
  }
}

/** A new step from what the globe shows, and the Khipu with any dataset it needs (the step is not yet in the path). */
export function stepFromGlobe(k: Khipu, scene: SoloScene, titles: SceneTitles = {}): { khipu: Khipu; step: KhipuStep } {
  const { datasets, parts } = sceneParts(k, scene, titles)
  const display = displayWithLook(undefined, scene)
  const step: KhipuStep = { id: freshId(k.path.steps), n: k.path.steps.length + 1, ...parts, ...(display ? { display } : {}), ...(scene.satellites?.on ? { satellites: { ...scene.satellites } } : {}), advance: { mode: 'manual' } }
  return { khipu: { ...k, datasets }, step }
}

/**
 * AYNI (owner, 2026-10-12): a step's display with the dataset's opacity and
 * eye as the globe has them; its palette and limits stay. Undefined when
 * there is nothing to say (no display, opaque and shown).
 */
function displayWithLook(display: unknown, scene: SoloScene): Record<string, unknown> | undefined {
  const { opacity: _o, hidden: _h, ...rest } = (display && typeof display === 'object' ? display : {}) as Record<string, unknown>
  const opacity = scene.look?.opacity ?? 1
  const hidden = !!scene.look?.hidden
  // A step that had a display says the dataset's look too, so a step after a hidden one shows it again.
  const say = opacity < 1 || hidden || display !== undefined
  if (!say) return undefined
  return { ...rest, opacity, hidden }
}

const renumber = (steps: KhipuStep[]): KhipuStep[] => steps.map((s, i) => (s.n === i + 1 ? s : { ...s, n: i + 1 }))
const withSteps = (k: Khipu, steps: KhipuStep[]): Khipu => ({ ...k, path: { ...k.path, steps: renumber(steps) } })

/** Put `step` after step `after` (-1: first; past the end: last). */
export function insertStep(k: Khipu, step: KhipuStep, after: number): Khipu {
  const steps = [...k.path.steps]
  const id = steps.some((s) => s.id === step.id) ? freshId(steps) : step.id
  steps.splice(Math.max(0, Math.min(steps.length, after + 1)), 0, { ...step, id })
  return withSteps(k, steps)
}

/** Move step `index` by `delta` places (clamped to the path). */
export function moveStep(k: Khipu, index: number, delta: number): Khipu {
  const steps = [...k.path.steps]
  const to = Math.max(0, Math.min(steps.length - 1, index + delta))
  if (to === index || !steps[index]) return k
  const [step] = steps.splice(index, 1)
  steps.splice(to, 0, step)
  return withSteps(k, steps)
}

/** Remove step `index`; the last step is never removed (a Khipu has at least one). */
export function removeStep(k: Khipu, index: number): Khipu {
  if (k.path.steps.length <= 1 || !k.path.steps[index]) return k
  return withSteps(k, k.path.steps.filter((_, i) => i !== index))
}

/** Change step `index`: a field set to undefined is removed; the rest is kept as it was. */
export function updateStep(k: Khipu, index: number, patch: Partial<KhipuStep>): Khipu {
  const step = k.path.steps[index]
  if (!step) return k
  const next: KhipuStep = { ...step }
  for (const [key, value] of Object.entries(patch)) {
    if (key === 'id' || key === 'n') continue
    if (value === undefined) delete next[key]
    else next[key] = value
  }
  const steps = [...k.path.steps]
  steps[index] = next
  return withSteps(k, steps)
}

/** Step `index` takes what the globe shows now: dataset, camera (with its rotation and tilt), date, overlay, spin and pins (its words stay). */
export function sceneToStep(k: Khipu, index: number, scene: SoloScene, titles: SceneTitles = {}): Khipu {
  if (!k.path.steps[index]) return k
  const step = k.path.steps[index]
  const { datasets, parts } = sceneParts(k, scene, titles, step.pins, step.layers)
  // The region gives way to the camera just taken; a step with no camera keeps what it framed.
  const display = displayWithLook(step.display, scene)
  // Khipu 1.2: the satellites as the globe has them (off: a step that had them says off).
  const satellites = scene.satellites?.on ? { ...scene.satellites, ...(step.satellites?.follow ? { follow: true } : {}) } : step.satellites ? { on: false } : undefined
  const patch: Partial<KhipuStep> = { ...parts, ...(parts.view ? { region: undefined } : {}), ...(display ? { display } : {}), ...(satellites ? { satellites } : {}) }
  // Fields the step left open stay open: the globe now decides them only where the step did.
  if (step.open?.length) {
    for (const field of ['dataset', 'view', 'time', 'layers', 'pins', 'display'] as const) if (step.open.includes(field)) delete patch[field]
    // The spin goes with the camera.
    if (step.open.includes('view')) delete patch.autorotate
  }
  return updateStep({ ...k, datasets }, index, patch)
}

/** The Khipu's datasets that no step and no log event uses any more, removed. */
export function pruneDatasets(k: Khipu): Khipu {
  const used = new Set<string>()
  for (const s of k.path.steps) {
    if (typeof s.dataset === 'string') used.add(s.dataset)
    for (const l of s.layers ?? []) used.add(l.dataset)
    // AYNI (multi-globe plan): the other globes' datasets are used too.
    for (const g of s.globes ?? []) if (typeof g.dataset === 'string') used.add(g.dataset)
  }
  // A log names datasets by key in its events: keep those too.
  const log = JSON.stringify(k.log ?? {})
  const datasets = k.datasets.filter((d) => used.has(d.key) || log.includes(`"${d.key}"`))
  return datasets.length === k.datasets.length ? k : { ...k, datasets }
}

/** What the editor touches before saving: the date it was changed, its languages, and datasets no longer used. */
export function finishEdit(k: Khipu, lang: string, now: number): Khipu {
  const languages = k.languages?.includes(lang) ? k.languages : [...(k.languages ?? []), lang]
  return pruneDatasets({ ...k, languages, modified: new Date(now).toISOString().replace(/\.\d{3}Z$/, 'Z') })
}

export interface EditProblem {
  /** The step's index, or -1 for the Khipu itself. */
  step: number
  key: 'edit.problem.title' | 'edit.problem.link' | 'edit.problem.seconds' | 'edit.problem.quiz' | 'edit.problem.quizAnswer'
}

/** What would make the Khipu invalid or unplayable, step by step. */
export function editProblems(k: Khipu): EditProblem[] {
  const out: EditProblem[] = []
  if (!Object.values(k.title ?? {}).some((v) => typeof v === 'string' && v.trim())) out.push({ step: -1, key: 'edit.problem.title' })
  k.path.steps.forEach((s, i) => {
    if ((s.media ?? []).some((m) => !/^https:\/\/[^\s/]+\.[^\s]+$/.test(m.url))) out.push({ step: i, key: 'edit.problem.link' })
    if (s.advance?.mode === 'timer' && !(typeof s.advance.seconds === 'number' && s.advance.seconds >= 1)) out.push({ step: i, key: 'edit.problem.seconds' })
    const quiz = s.quiz as { question?: KhipuText; options?: KhipuText[]; correctIndex?: number } | undefined
    if (quiz) {
      const filled = (t?: KhipuText) => Object.values(t ?? {}).some((v) => typeof v === 'string' && v.trim())
      if (!filled(quiz.question) || !Array.isArray(quiz.options) || quiz.options.length < 2 || quiz.options.length > 4 || !quiz.options.every(filled)) {
        out.push({ step: i, key: 'edit.problem.quiz' })
      } else if (typeof quiz.correctIndex !== 'number' || quiz.correctIndex < 0 || quiz.correctIndex >= quiz.options.length) {
        out.push({ step: i, key: 'edit.problem.quizAnswer' })
      }
    }
  })
  return out
}

/** A new, empty story to build step by step from the globe. */
export function emptyStory(id: string, title: KhipuText, lang: string, now: number): Khipu {
  const at = new Date(now).toISOString().replace(/\.\d{3}Z$/, 'Z')
  return {
    format: 'ayni-khipu',
    schemaVersion: KHIPU_SCHEMA_VERSION,
    id,
    kind: 'story',
    status: 'draft',
    title,
    languages: [lang],
    created: at,
    modified: at,
    privacy: { mode: 'anonymous' },
    datasets: [],
    path: { steps: [{ id: 's1', n: 1, dataset: null, advance: { mode: 'manual' } }] },
  }
}
